import React, { useState, useMemo } from 'react'
import {
  Row, Col, DatePicker, Select, Table, Button, Space, Tag, Typography,
  Descriptions, Popconfirm, message, Tooltip, InputNumber, Card, Modal, Input,
} from 'antd'
import { PlusOutlined, EditOutlined, DeleteOutlined, ApartmentOutlined, CameraOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { WorkOrder, Intervention } from '@/types'
import NuovoInterventoModal from '@/components/NuovoInterventoModal'
import RdaInterventoModal from '@/components/RdaInterventoModal'
import { useRdasForWorkOrder } from '@/hooks/useRdaIntervento'
import { useInterventionStatusTypes } from '@/hooks/useSystemTables'
import { usePermission } from '@/hooks/usePermission'
import { useAuth } from '@/contexts/AuthContext'
import axiosInstance from '@/lib/axios'
import AssegnaOfficinaModal from '@/components/AssegnaOfficinaModal'
import { useWorkshopQuotes } from '@/hooks/useWorkshopQuotes'
import type { WorkshopQuote } from '@/types/workshopQuotes'
import BloccoRdaIntervento, { RDO_ORDINATA_STATUSES } from '@/components/shared/BloccoRdaIntervento'
import BloccoPreventiviOfficina from '@/components/shared/BloccoPreventiviOfficina'
export { getQuoteTag } from '@/components/shared/BloccoPreventiviOfficina'

const { Text } = Typography

interface FaseAnalisiProps {
  workOrder: WorkOrder
  users: { value: number; label: string }[]
  /** Se true, i campi Data Entrata Veicolo e Tecnico Analisi sono editabili.
   *  Richiede work_orders.edit. Senza il permesso: sola lettura. */
  canEditWO: boolean
  onWorkOrderUpdate: (patch: Partial<WorkOrder>) => void
  onInterventionsChange: () => void
  /** Apre l'acquisizione foto con fase='lavorazione' e l'intervento già selezionato.
   *  Omettendo la prop, il pulsante fotocamera non compare. */
  onPhotoCaptureIntervention?: (interventionId: number) => void
}

const MODALITA_LABELS: Record<number, string> = {
  1: 'Riparazione',
  2: 'Acquisto',
}


const FaseAnalisi: React.FC<FaseAnalisiProps> = ({
  workOrder,
  users,
  canEditWO,
  onWorkOrderUpdate,
  onInterventionsChange,
  onPhotoCaptureIntervention,
}) => {
  const [interventoModalOpen, setInterventoModalOpen] = useState(false)
  const [editingIntervento, setEditingIntervento] = useState<Intervention | null>(null)
  const [rdaAfterNew, setRdaAfterNew] = useState<{ open: boolean; interventionId: number; descrizione: string; note: string | null }>({ open: false, interventionId: 0, descrizione: '', note: null })
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [savingOreId, setSavingOreId] = useState<number | null>(null)
  const [savingStatoId, setSavingStatoId] = useState<number | null>(null)
  const [notaModal, setNotaModal] = useState<{ interventionId: number; statoId: number } | null>(null)
  const [notaText, setNotaText] = useState('')
  // Consenso avvertito (§5): ripresa di un intervento fermo per una RDA ancora pendente
  const [consensoModal, setConsensoModal] = useState<{ interventionId: number; statoId: number; rdaCodice: string; rdaFornitore: string | null } | null>(null)
  const [consensoText, setConsensoText] = useState('')

  const { data: interventionStatuses } = useInterventionStatusTypes()
  const { user } = useAuth()
  const isWorkshopRole = (user as any)?.ruolo === 'WORKSHOP'
  const canEditStato = usePermission('work_orders.edit') || usePermission('interventions.update_status')
  const canAssign = usePermission('workshop_quotes.request') && !isWorkshopRole
  const canViewQuotes = usePermission('workshop_quotes.view')
  const canApprove = usePermission('workshop_quotes.approve')
  const canCreatePhotos = usePermission('work_order_photos.create')
  const canEditIntervention = usePermission('interventions.edit')
  const canDeleteIntervention = usePermission('interventions.delete')
  const canCreateIntervention = usePermission('interventions.create')
  const canUpdateStatus = usePermission('interventions.update_status')
  // Copertura sinistro: modificabile solo da amministrazione/direzione
  const canEditSinistro = usePermission('work_orders.edit')

  const [assignModal, setAssignModal] = useState<{ interventionId: number; currentWorkshopId: number | null } | null>(null)
  const { data: workshopQuotes = [] } = useWorkshopQuotes(workOrder.id, { enabled: canViewQuotes })

  const interventionQuoteMap = useMemo(() => {
    const map = new Map<number, WorkshopQuote[]>()
    for (const q of workshopQuotes) {
      const existing = map.get(q.intervention_id) ?? []
      map.set(q.intervention_id, [...existing, q])
    }
    return map
  }, [workshopQuotes])

  // rdaChecks: purchaseRequestId → itemId → checked
  const [rdaChecks, setRdaChecks] = useState<Record<number, Record<number, boolean>>>({})
  const [rdaDates, setRdaDates] = useState<Record<number, Record<number, ReturnType<typeof dayjs> | null>>>({})
  const [savingRda, setSavingRda] = useState<Record<number, boolean>>({})

  const interventions = [...(workOrder.interventions || [])].sort(
    (a, b) => (a.progressivo ?? a.id ?? 0) - (b.progressivo ?? b.id ?? 0)
  )

  // Sistema vecchio (rdas/rda_items)
  const { data: workOrderRdas } = useRdasForWorkOrder(workOrder.id)
  const interventionRdaMap = useMemo(() => {
    const map: Record<number, { id: number; note?: string | null; items: { id: number; descrizione: string }[] }> = {}
    for (const rda of workOrderRdas ?? []) {
      if (rda.intervention_id) map[rda.intervention_id] = rda
    }
    return map
  }, [workOrderRdas])

  const expandedRowKeys = useMemo(() => {
    return interventions
      .filter(r => {
        if (!r.id) return false
        const rdaData = interventionRdaMap[r.id]
        const rdaItems = rdaData?.items ?? []
        const prList: any[] = (r as any).purchase_requests ?? []
        const prOrdinati = prList.filter((pr: any) => {
          const nome = (pr.purchase_request_status_nome ?? '').toLowerCase()
          return RDO_ORDINATA_STATUSES.some(s => nome.includes(s))
        })
        const hasMateriali = rdaItems.length > 0 || prOrdinati.length > 0
        const hasQuotes = canViewQuotes && (interventionQuoteMap.get(r.id) ?? []).length > 0
        return hasMateriali || hasQuotes
      })
      .map(r => r.id as number)
  }, [interventions, interventionRdaMap, interventionQuoteMap, canViewQuotes])

  const handleSaveAnalisiFields = async (patch: Partial<WorkOrder>) => {
    try {
      await axiosInstance.put(`/work-orders/${workOrder.id}`, patch)
      onWorkOrderUpdate(patch)
    } catch {
      message.error('Salvataggio fallito')
    }
  }

  const handleInterventoConfirm = async (data: any) => {
    try {
      if (editingIntervento) {
        await axiosInstance.put(
          `/work-orders/${workOrder.id}/interventions/${editingIntervento.id}`,
          data
        )
        onInterventionsChange()
        setInterventoModalOpen(false)
        setEditingIntervento(null)
      } else {
        const res = await axiosInstance.post(`/work-orders/${workOrder.id}/interventions`, data)
        onInterventionsChange()
        setInterventoModalOpen(false)
        setEditingIntervento(null)
        const newId = res.data?.id
        if (newId) {
          Modal.confirm({
            title: 'Intervento salvato',
            content: 'Vuoi gestire subito la Richiesta di Acquisto?',
            okText: 'Sì, gestisci RDA',
            cancelText: 'No, chiudi',
            onOk: () => setRdaAfterNew({ open: true, interventionId: newId, descrizione: data.descrizione_intervento, note: data.note_intervento ?? null }),
          })
        }
      }
    } catch {
      message.error('Salvataggio intervento fallito')
    }
  }

  const handleSaveOreEffettive = async (interventionId: number, ore: number | null) => {
    setSavingOreId(interventionId)
    try {
      if (canEditIntervention) {
        await axiosInstance.put(
          `/work-orders/${workOrder.id}/interventions/${interventionId}`,
          { ore_effettive: ore }
        )
      } else {
        const iv = workOrder.interventions?.find(i => i.id === interventionId)
        if (!iv || iv.stato_intervento_id == null) {
          message.error('Impossibile salvare: stato intervento non disponibile')
          return
        }
        const currentStatus = (interventionStatuses as any[])?.find((s: any) => s.id === iv.stato_intervento_id)
        if (currentStatus?.codice === 'sospeso' && !iv.note_sospensione) {
          message.error('Aggiungi prima la nota di sospensione')
          return
        }
        const payload: Record<string, unknown> = {
          stato_intervento_id: iv.stato_intervento_id,
          ore_effettive: ore,
        }
        if (iv.note_sospensione != null) {
          payload.note_sospensione = iv.note_sospensione
        }
        await axiosInstance.patch(
          `/work-orders/${workOrder.id}/interventions/${interventionId}/status`,
          payload,
        )
      }
      onInterventionsChange()
    } catch {
      message.error('Salvataggio ore effettive fallito')
    } finally {
      setSavingOreId(null)
    }
  }

  const handleSaveStato = async (interventionId: number, statoId: number, nota?: string, motivazione?: string) => {
    const status = (interventionStatuses as any[])?.find((s: any) => s.id === statoId)
    if (status?.richiede_nota && !nota) {
      setNotaText('')
      setNotaModal({ interventionId, statoId })
      return
    }
    // Consenso avvertito: l'intervento e' fermo perche' manca il materiale e la
    // RDA e' ancora pendente. Si puo' riprendere, ma sapendo cosa si scavalca.
    const ivCorrente = workOrder.interventions?.find(i => i.id === interventionId)
    const statoPrima = (interventionStatuses as any[])?.find((s: any) => s.id === ivCorrente?.stato_intervento_id)
    if (
      !motivazione &&
      statoPrima?.codice === 'sospeso' &&
      status?.codice !== 'sospeso' &&
      ivCorrente?.sospensione_origine === 'rda' &&
      ivCorrente?.rda_pendente
    ) {
      setConsensoText('')
      setConsensoModal({
        interventionId,
        statoId,
        rdaCodice: ivCorrente.rda_pendente.codice,
        rdaFornitore: ivCorrente.rda_pendente.fornitore ?? null,
      })
      return
    }
    setSavingStatoId(interventionId)
    try {
      await axiosInstance.patch(
        `/work-orders/${workOrder.id}/interventions/${interventionId}/status`,
        {
          stato_intervento_id: statoId,
          ...(nota ? { note_sospensione: nota } : {}),
          ...(motivazione ? { motivazione_ripresa: motivazione } : {}),
        }
      )
      onInterventionsChange()
    } catch (error: any) {
      // Il detail del backend spiega il rifiuto (es. il vincolo della RDA).
      message.error(error?.response?.data?.detail || 'Salvataggio stato fallito')
    } finally {
      setSavingStatoId(null)
    }
  }

  const handleNotaConfirm = () => {
    if (!notaModal) return
    if (!notaText.trim()) { message.warning('Inserisci un motivo'); return }
    const { interventionId, statoId } = notaModal
    setNotaModal(null)
    handleSaveStato(interventionId, statoId, notaText.trim())
  }

  const handleConsensoConfirm = () => {
    if (!consensoModal) return
    if (!consensoText.trim()) { message.warning('Inserisci una motivazione'); return }
    const { interventionId, statoId } = consensoModal
    setConsensoModal(null)
    handleSaveStato(interventionId, statoId, undefined, consensoText.trim())
  }

  const handleDeleteIntervento = async (interventionId: number) => {
    setDeletingId(interventionId)
    try {
      await axiosInstance.delete(`/work-orders/${workOrder.id}/interventions/${interventionId}`)
      onInterventionsChange()
    } catch {
      message.error('Eliminazione fallita')
    } finally {
      setDeletingId(null)
    }
  }

  function toggleItemCheck(prId: number, itemId: number, checked: boolean) {
    setRdaChecks(prev => ({
      ...prev,
      [prId]: { ...(prev[prId] ?? {}), [itemId]: checked },
    }))
    if (checked) {
      setRdaDates(prev => ({
        ...prev,
        [prId]: { ...(prev[prId] ?? {}), [itemId]: dayjs() },
      }))
    }
  }

  async function handleSaveRdaReceive(pr: any) {
    const itemChecks = rdaChecks[pr.id] ?? {}
    const dates = rdaDates[pr.id] ?? {}
    const items: any[] = pr.items ?? []
    const newlyChecked = items.filter((item: any) => !item.ricevuto && itemChecks[item.id])
    if (newlyChecked.length === 0) return

    const missingDate = newlyChecked.some((item: any) => !dates[item.id])
    if (missingDate) {
      message.warning('Imposta la data di ricezione per tutti gli articoli spuntati')
      return
    }

    setSavingRda(prev => ({ ...prev, [pr.id]: true }))
    try {
      for (const item of newlyChecked) {
        await axiosInstance.patch(
          `/purchase-requests/${pr.id}/items/${item.id}/receive`,
          { data_ricezione_item: dates[item.id]!.toISOString() }
        )
      }
      onInterventionsChange()
      message.success('Ricezione registrata')
    } catch {
      message.error('Errore nella registrazione della ricezione')
    } finally {
      setSavingRda(prev => ({ ...prev, [pr.id]: false }))
    }
  }


  const columns = [
    {
      title: '#',
      dataIndex: 'progressivo',
      width: 36,
      onCell: () => ({}),
      render: (v: number) => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>,
    },
    {
      title: 'Descrizione',
      dataIndex: 'descrizione_intervento',
      render: (v: string, record: Intervention) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Text style={{ fontSize: 12 }}>{v}</Text>
          </div>
          {record.note_intervento && (
            <Text type="secondary" style={{ fontSize: 11 }}>Note: {record.note_intervento}</Text>
          )}
          {record.note_sospensione && (
            <span style={{ fontSize: 11, color: '#d46b08', display: 'block' }}>Intervento sospeso: {record.note_sospensione}</span>
          )}
          {record.coperto_sinistro && (
            <Tag color="orange" style={{ fontSize: 10, margin: 0, padding: '0 5px', lineHeight: '16px' }}>
              🔒 Intervento per sinistro n° {workOrder.numero_sinistro ?? '—'}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Mod.',
      dataIndex: 'modalita_risoluzione_id',
      width: 90,
      render: (v: number) => (
        <Tag color={v === 2 ? 'orange' : 'blue'} style={{ fontSize: 11 }}>
          {MODALITA_LABELS[v] || '—'}
        </Tag>
      ),
    },
    {
      title: 'Ore Stim.',
      dataIndex: 'durata_stimata',
      width: 72,
      render: (v: number) => (
        <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {v ? `${v}h` : '—'}
        </Text>
      ),
    },
    {
      title: 'Ore eff.',
      width: 100,
      render: (_: any, record: Intervention) => (
        <Tooltip title="Modifica e premi Tab o clicca fuori per salvare">
          <InputNumber
            size="small"
            min={0}
            max={99.99}
            step={0.5}
            precision={2}
            value={record.ore_effettive ?? undefined}
            placeholder="—"
            disabled={savingOreId === record.id || (!canEditIntervention && !canUpdateStatus)}
            onBlur={(e) => {
              const raw = e.target.value.replace(',', '.')
              const val = raw === '' ? null : parseFloat(raw)
              const current = record.ore_effettive ?? null
              if (val !== current) handleSaveOreEffettive(record.id!, val)
            }}
            style={{ width: 80, fontSize: 12 }}
            controls={false}
            addonAfter="h"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Stato',
      width: 140,
      render: (_: any, record: Intervention) => (
        <Select
          size="small"
          style={{ width: 130 }}
          value={record.stato_intervento_id ?? undefined}
          placeholder="Non iniziato"
          disabled={!canEditStato || savingStatoId === record.id}
          loading={savingStatoId === record.id}
          onChange={(val: number) => handleSaveStato(record.id!, val)}
          options={(interventionStatuses as any[])?.map((s: any) => ({ value: s.id, label: s.nome }))}
        />
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_intervento',
      width: 90,
      render: (v: string) => v ? (
        <Tag color={v === 'Meccanico' ? 'blue' : 'orange'} style={{ fontSize: 11 }}>{v}</Tag>
      ) : null,
    },
    {
      title: 'Officina',
      width: 120,
      render: (_: any, record: Intervention) => {
        const workshopNome = (record as any).workshop_nome
        const relevantQuote = record.id ? interventionQuoteMap.get(record.id)?.find(q =>
          ['inviata', 'sopralluogo_richiesto', 'risposta_ricevuta', 'approvata'].includes(q.stato)
        ) : undefined
        if (relevantQuote) {
          return <Text style={{ fontSize: 11 }}>{relevantQuote.workshop_nome ?? workshopNome ?? '—'}</Text>
        }
        if (!workshopNome && !canAssign) return null
        return (
          <Tooltip title={canAssign ? 'Assegna a officina esterna' : undefined}>
            <Tag
              color={workshopNome ? 'orange' : 'default'}
              icon={<ApartmentOutlined />}
              style={{ cursor: canAssign ? 'pointer' : 'default', fontSize: 11 }}
              onClick={canAssign ? () => {
                setAssignModal({ interventionId: record.id!, currentWorkshopId: (record as any).workshop_id ?? null })
              } : undefined}
            >
              {workshopNome ?? 'Assegna'}
            </Tag>
          </Tooltip>
        )
      },
    },
    {
      title: '',
      width: 92,
      render: (_: any, record: Intervention) => (
        <Space size={4}>
          {/* Icona fotocamera: apre l'acquisizione con fase='lavorazione'
              e l'intervento già valorizzato. Visibile solo con
              work_order_photos.create e se il callback è passato. */}
          {onPhotoCaptureIntervention && canCreatePhotos && record.id != null && (
            <Tooltip title="Fotografa questo intervento">
              <Button
                size="small"
                icon={<CameraOutlined />}
                onClick={() => onPhotoCaptureIntervention(record.id!)}
              />
            </Tooltip>
          )}
          {canEditIntervention && (
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => { setEditingIntervento(record); setInterventoModalOpen(true) }}
            />
          )}
          {!isWorkshopRole && canDeleteIntervention && (
            <Popconfirm
              title="Eliminare questo intervento?"
              onConfirm={() => handleDeleteIntervento(record.id!)}
              okText="Sì"
              cancelText="No"
            >
              <Button
                size="small"
                icon={<DeleteOutlined />}
                danger
                loading={deletingId === record.id}
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  return (
    <div style={{ padding: '0 24px 24px' }}>
      {/* Campi analisi */}
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Data cambio in analisi">
              <Text style={{ fontSize: 13 }}>
                {workOrder.data_cambio_stato_analisi
                  ? dayjs(workOrder.data_cambio_stato_analisi).format('DD/MM/YYYY HH:mm')
                  : '—'}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} sm={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Data Inizio Analisi">
              <Text style={{ fontSize: 13 }}>
                {workOrder.data_inizio_analisi
                  ? dayjs(workOrder.data_inizio_analisi).format('DD/MM/YYYY')
                  : '—'}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} sm={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Data Entrata Veicolo">
              {canEditWO ? (
                <DatePicker
                  showTime={{ format: 'HH:mm', defaultValue: dayjs('08:30', 'HH:mm') }}
                  size="small"
                  value={workOrder.data_entrata_veicolo ? dayjs(workOrder.data_entrata_veicolo) : null}
                  onChange={(date) => {
                    if (date) {
                      const appuntamento = workOrder.data_appuntamento ? dayjs(workOrder.data_appuntamento) : null
                      const completamento = workOrder.data_completamento ? dayjs(workOrder.data_completamento) : null
                      if (appuntamento && date.isBefore(appuntamento, 'minute')) {
                        message.warning('La data entrata veicolo non può essere prima della data appuntamento')
                        return
                      }
                      if (completamento && !date.isBefore(completamento, 'minute')) {
                        message.warning('La data entrata veicolo deve essere prima della data completamento')
                        return
                      }
                    }
                    handleSaveAnalisiFields({
                      data_entrata_veicolo: date ? date.toISOString() : null,
                    })
                  }}
                  format="DD/MM/YYYY HH:mm"
                  style={{ width: 170 }}
                  placeholder="Seleziona data e ora"
                />
              ) : (
                <span>
                  {workOrder.data_entrata_veicolo
                    ? dayjs(workOrder.data_entrata_veicolo).format('DD/MM/YYYY HH:mm')
                    : '—'}
                </span>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Col>
        <Col xs={24} sm={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Tecnico Analisi">
              {canEditWO ? (
                <Select
                  size="small"
                  allowClear
                  placeholder="Seleziona tecnico..."
                  style={{ width: 200 }}
                  value={workOrder.tecnico_analisi_id ?? undefined}
                  options={users}
                  onChange={(val) => handleSaveAnalisiFields({ tecnico_analisi_id: val ?? null })}
                />
              ) : (
                <span>
                  {`${workOrder.tecnico_analisi?.nome || ''} ${workOrder.tecnico_analisi?.cognome || ''}`.trim() || '—'}
                </span>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>

      {/* Interventi */}
      <Card size="small" style={{ marginTop: 8 }}>
        <div style={{ marginBottom: 8 }}>
          <Space style={{ justifyContent: 'space-between', width: '100%' }}>
            <Text strong style={{ fontSize: 13 }}>Interventi Tecnici</Text>
            {!isWorkshopRole && canCreateIntervention && (
              <Button
                size="small"
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => { setEditingIntervento(null); setInterventoModalOpen(true) }}
              >
                Intervento
              </Button>
            )}
          </Space>
        </div>

        <style>{`.fase-analisi-table .ant-table-tbody > tr > td { vertical-align: top !important; padding-top: 6px !important; }`}</style>
        <Table
          className="fase-analisi-table"
          dataSource={interventions}
          columns={columns}
          rowKey="id"
          size="small"
          pagination={false}
          locale={{ emptyText: 'Nessun intervento — aggiungine uno con il bottone sopra' }}
          onRow={() => ({
            style: { outline: '1px solid var(--ant-color-border)', outlineOffset: '-1px' },
          })}
          expandable={{
            expandedRowKeys,
            showExpandColumn: false,
            rowExpandable: (record) => expandedRowKeys.includes(record.id as number),
            expandedRowRender: (record) => (
              <Row gutter={12} style={{ padding: '4px 0' }}>
                <Col xs={24} lg={12}>
                  <BloccoRdaIntervento
                    record={record}
                    interventionRdaMap={interventionRdaMap}
                    rdaChecks={rdaChecks}
                    rdaDates={rdaDates}
                    savingRda={savingRda}
                    onToggleItemCheck={toggleItemCheck}
                    onDateChange={(prId, itemId, date) =>
                      setRdaDates(prev => ({
                        ...prev,
                        [prId]: { ...(prev[prId] ?? {}), [itemId]: date },
                      }))
                    }
                    onSaveRdaReceive={handleSaveRdaReceive}
                  />
                </Col>
                <Col xs={24} lg={12}>
                  <BloccoPreventiviOfficina
                    workOrderId={workOrder.id!}
                    quotes={interventionQuoteMap.get(record.id ?? 0) ?? []}
                    canViewQuotes={canViewQuotes}
                    canApprove={canApprove}
                  />
                </Col>
              </Row>
            ),
          }}
        />
      </Card>

      <NuovoInterventoModal
        open={interventoModalOpen}
        onClose={() => { setInterventoModalOpen(false); setEditingIntervento(null) }}
        onConfirm={handleInterventoConfirm}
        initialData={editingIntervento ? {
          descrizione_intervento: editingIntervento.descrizione_intervento,
          modalita_risoluzione_id: editingIntervento.modalita_risoluzione_id ?? 1,
          tipo_intervento: editingIntervento.tipo_intervento,
          durata_stimata: editingIntervento.durata_stimata,
          note_intervento: editingIntervento.note_intervento ?? null,
          ore_effettive: editingIntervento.ore_effettive ?? null,
          coperto_sinistro: editingIntervento.coperto_sinistro ?? false,
        } : undefined}
        interventionId={editingIntervento?.id}
        workOrderId={workOrder.id}
        workOrder={workOrder}
        canEditSinistro={canEditSinistro}
      />

      <Modal
        open={!!notaModal}
        title="Motivo richiesto"
        okText="Conferma"
        cancelText="Annulla"
        onOk={handleNotaConfirm}
        onCancel={() => setNotaModal(null)}
      >
        <Input.TextArea
          rows={3}
          placeholder="Inserisci il motivo..."
          value={notaText}
          onChange={e => setNotaText(e.target.value)}
          autoFocus
        />
      </Modal>

      <Modal
        open={!!consensoModal}
        title="Intervento in attesa di materiali"
        okText="Riprendi comunque"
        cancelText="Annulla"
        okButtonProps={{ danger: true, disabled: !consensoText.trim() }}
        onOk={handleConsensoConfirm}
        onCancel={() => setConsensoModal(null)}
      >
        <p style={{ marginBottom: 8 }}>Questo intervento è fermo in attesa di materiali.</p>
        <p style={{ marginBottom: 8, fontWeight: 600, color: '#d46b08' }}>
          RDA {consensoModal?.rdaCodice}
          {consensoModal?.rdaFornitore ? ` — ${consensoModal.rdaFornitore}` : ''}, approvata e non ancora ricevuta.
        </p>
        <p style={{ marginBottom: 8, color: '#8c8c8c' }}>
          Riprendendolo scavalchi questo vincolo. Scrivi perché: la motivazione resta
          registrata nello storico della scheda.
        </p>
        <Input.TextArea
          rows={3}
          placeholder="Motivo della ripresa..."
          value={consensoText}
          onChange={e => setConsensoText(e.target.value)}
          autoFocus
        />
      </Modal>

      {rdaAfterNew.interventionId > 0 && (
        <RdaInterventoModal
          open={rdaAfterNew.open}
          onClose={() => setRdaAfterNew({ open: false, interventionId: 0, descrizione: '', note: null })}
          interventionId={rdaAfterNew.interventionId}
          workOrderId={workOrder.id}
          interventionInfo={{ descrizione: rdaAfterNew.descrizione, note: rdaAfterNew.note }}
        />
      )}

      <AssegnaOfficinaModal
        interventionId={assignModal?.interventionId ?? 0}
        workOrderId={workOrder.id}
        currentWorkshopId={assignModal?.currentWorkshopId ?? null}
        interventions={interventions}
        open={!!assignModal}
        onClose={() => setAssignModal(null)}
        onSuccess={onInterventionsChange}
      />

    </div>
  )
}

export default FaseAnalisi
